<script setup>
/**
 * Travel provider call history.
 *
 * This screen answers one question: "why did that search fail?". It deliberately shows
 * no passenger data, no record locators and no PCC - the underlying table cannot store
 * them (ADR-006), and the columns here are the ones that actually help someone
 * debugging: what was called, how long it took, what came back, and the trace id
 * Travelport support asks for.
 */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import TravelRequestEventsAPI from 'dashboard/api/travel/requestEvents';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const events = ref([]);
const meta = ref({ current_page: 1, total_entries: 0, per_page: 25 });
const isLoading = ref(false);

const fetchPage = async (page = 1) => {
  isLoading.value = true;
  try {
    const { data } = await TravelRequestEventsAPI.get({ page });
    events.value = data.events;
    meta.value = data.meta;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAVEL.HISTORY.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => fetchPage());

const hasNextPage = () =>
  meta.value.current_page * meta.value.per_page < meta.value.total_entries;

// An HTTP status is the honest signal here: green for a 2xx, red otherwise, grey when
// the call never reached the provider at all (a timeout has no status).
const statusClass = event => {
  if (!event.http_status) return 'bg-n-slate-3 text-n-slate-11';
  return event.http_status < 400
    ? 'bg-n-teal-3 text-n-teal-11'
    : 'bg-n-ruby-3 text-n-ruby-11';
};

const formatTime = value => new Date(value).toLocaleString();
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="w-full max-w-5xl px-6 py-6 mx-auto">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('TRAVEL.HISTORY.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('TRAVEL.HISTORY.SUBTITLE') }}
      </p>

      <div
        v-if="events.length"
        class="mt-5 overflow-hidden border rounded-xl border-n-weak"
      >
        <table class="w-full text-sm">
          <thead class="bg-n-solid-2 text-n-slate-11">
            <tr>
              <th class="px-4 py-2 font-medium ltr:text-left rtl:text-right">
                {{ t('TRAVEL.HISTORY.COLUMNS.OPERATION') }}
              </th>
              <th class="px-4 py-2 font-medium ltr:text-left rtl:text-right">
                {{ t('TRAVEL.HISTORY.COLUMNS.RESULT') }}
              </th>
              <th class="px-4 py-2 font-medium ltr:text-left rtl:text-right">
                {{ t('TRAVEL.HISTORY.COLUMNS.DURATION') }}
              </th>
              <th class="px-4 py-2 font-medium ltr:text-left rtl:text-right">
                {{ t('TRAVEL.HISTORY.COLUMNS.WHEN') }}
              </th>
              <th class="px-4 py-2 font-medium ltr:text-left rtl:text-right">
                {{ t('TRAVEL.HISTORY.COLUMNS.TRACE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="event in events"
              :key="event.id"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-2 text-n-slate-12">
                {{
                  t(
                    `TRAVEL.HISTORY.OPERATIONS.${event.operation.toUpperCase()}`
                  )
                }}
              </td>
              <td class="px-4 py-2">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="statusClass(event)"
                >
                  {{
                    event.http_status ||
                    t(
                      `TRAVEL.HISTORY.ERRORS.${(event.error_code || 'unknown').toUpperCase()}`
                    )
                  }}
                </span>
              </td>
              <td class="px-4 py-2 text-n-slate-11">
                {{
                  t('TRAVEL.HISTORY.MILLISECONDS', { value: event.duration_ms })
                }}
              </td>
              <td class="px-4 py-2 text-n-slate-11">
                {{ formatTime(event.created_at) }}
              </td>
              <td class="px-4 py-2 font-mono text-xs truncate text-n-slate-10">
                {{ event.trace_id }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p
        v-else-if="!isLoading"
        class="mt-6 text-sm text-center text-n-slate-11"
      >
        {{ t('TRAVEL.HISTORY.EMPTY') }}
      </p>

      <div v-if="events.length" class="flex items-center justify-between mt-4">
        <p class="text-xs text-n-slate-10">
          {{ t('TRAVEL.HISTORY.TOTAL', { count: meta.total_entries }) }}
        </p>
        <div class="flex gap-2">
          <Button
            :label="t('TRAVEL.HISTORY.PREVIOUS')"
            variant="faded"
            size="sm"
            :disabled="meta.current_page <= 1 || isLoading"
            @click="fetchPage(meta.current_page - 1)"
          />
          <Button
            :label="t('TRAVEL.HISTORY.NEXT')"
            variant="faded"
            size="sm"
            :disabled="!hasNextPage() || isLoading"
            @click="fetchPage(meta.current_page + 1)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
